// Out-of-Office awareness getters
//
// Walks state.events for type === "ooo" CalendarEvents (including any
// recurrence instances) and answers two questions:
//   - is a given user OOO at a given moment?
//   - if so, what is the surrounding range so we can show "back on X"?
//
// Relies on LISTEN_EVENTS (events-ooo) being active for employees.

const eventInstances = (event) => {
  if (event.data.instances && Object.keys(event.data.instances).length) {
    return Object.values(event.data.instances).map((inst) => ({
      start: inst.start,
      end: inst.end,
    }));
  }
  return [{ start: event.data.start, end: event.data.end }];
};

const dateInRange = (date, range) => {
  if (!range.start || !range.end) return false;
  return date >= range.start && date < range.end;
};

// Everyone's OOO ranges, from one pass over state.events. `state.events` is
// shared with the calendar and every page that lists events, so it can hold
// thousands; the per-user getters below are asked once per recipient chip,
// OOO indicator and task-assign warning, and each used to rescan all of it.
// A value getter is cached by Vuex until state.events changes.
//
// `everyone` holds the ranges of events that apply to all users.
export const oooRangesByUser = (state) => {
  const byUser = { everyone: [], users: {} };
  Object.values(state.events || {}).forEach((event) => {
    if (!event || event.data.type !== "ooo") return;
    const ranges = eventInstances(event).filter(
      (range) => range.start && range.end
    );
    if (!ranges.length) return;
    if (event.data.everyone) {
      byUser.everyone.push(...ranges);
      return;
    }
    (event.data.participants || []).forEach((userID) => {
      (byUser.users[userID] = byUser.users[userID] || []).push(...ranges);
    });
  });
  return byUser;
};

const userOOORanges = (getters, userID) => {
  if (!userID) return [];
  const { everyone, users } = getters.oooRangesByUser;
  return users[userID] ? [...users[userID], ...everyone] : everyone;
};

export const isUserOOO = (state, getters) => (userID, date) => {
  const when = date instanceof Date ? date : new Date();
  return userOOORanges(getters, userID).some((range) =>
    dateInRange(when, range)
  );
};

export const userOOORange = (state, getters) => (userID, date) => {
  const when = date instanceof Date ? date : new Date();
  return (
    userOOORanges(getters, userID).find((range) => dateInRange(when, range)) ||
    null
  );
};

// Returns the next OOO range starting after `date` (or null if none).
// Useful for warning when a task's due date falls inside an upcoming OOO.
export const nextUserOOORange = (state, getters) => (userID, date) => {
  const when = date instanceof Date ? date : new Date();
  const upcoming = userOOORanges(getters, userID)
    .filter((range) => range.end > when)
    .sort((a, b) => a.start - b.start);
  return upcoming[0] || null;
};
